import { pushWithRegion } from 'actions/NavigationActions'
import { trackLoyaltyActionRewardQuestClick } from 'analytics/snowplow/events/loyaltyActionRewardQuestClickEvents'
import Clickable from 'components/Common/Clickable'
import {
  ACTION_REWARD_CONFIGS,
  type SupportedLoyaltyActionReward,
} from 'loyalty/constants/loyaltyActionRewards'
import getLoyaltyActionRewardCTANav from 'loyalty/lib/getLoyaltyActionRewardCTANav'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import LoyaltyActionRewardsListItemContent from './LoyaltyActionRewardsListItemContent'

const InteractiveListItemRow = styled(Clickable)`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: inherit;
  text-decoration: none;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
`

interface Props {
  reward: SupportedLoyaltyActionReward
  tier: App.LoyaltyTier
  itemPosition: number
}

function LoyaltyActionRewardsListItem({ reward, tier, itemPosition }: Props) {
  const dispatch = useAppDispatch()
  const osName = useAppSelector(state => state.system.device.osName)
  const userAgent = useAppSelector(state => state.system.rawUserAgentString)

  const declaration = ACTION_REWARD_CONFIGS[reward.actionName]
  const { isComplete, title, description } = reward

  const ctaNav = !isComplete ? declaration.ctaNav : undefined
  const resolvedNav = useMemo(
    () => (ctaNav ? getLoyaltyActionRewardCTANav(ctaNav, { osName, userAgent }) : undefined),
    [ctaNav, osName, userAgent],
  )
  const showChevron = !isComplete && !!resolvedNav

  const rowContentProps = useMemo(() => ({
    reward,
    tier,
    title,
    description,
    showChevron,
  }), [reward, tier, title, description, showChevron])

  const handleQuestActivate = useCallback(() => {
    if (!resolvedNav) return

    trackLoyaltyActionRewardQuestClick(reward, resolvedNav, itemPosition)

    if (resolvedNav.kind === 'route') {
      dispatch(pushWithRegion(resolvedNav.to))
    }
  }, [dispatch, itemPosition, resolvedNav, reward])

  if (resolvedNav?.kind === 'external') {
    return (
      <InteractiveListItemRow
        href={resolvedNav.href}
        target="_blank"
        rel="noopener"
        onClick={handleQuestActivate}
      >
        <LoyaltyActionRewardsListItemContent {...rowContentProps} />
      </InteractiveListItemRow>
    )
  }

  if (resolvedNav?.kind === 'route') {
    return (
      <InteractiveListItemRow
        type="button"
        onClick={handleQuestActivate}
      >
        <LoyaltyActionRewardsListItemContent {...rowContentProps} />
      </InteractiveListItemRow>
    )
  }

  return <LoyaltyActionRewardsListItemContent {...rowContentProps} />
}

export default LoyaltyActionRewardsListItem
